import { useState, useEffect, useRef, useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

interface FlowPreviewModalProps {
  nodes: Node[];
  edges: Edge[];
  onClose: () => void;
}

interface Message {
  id: string;
  type: 'bot' | 'user';
  content: string;
  timestamp: Date;
  mediaUrl?: string;
  mediaType?: 'image' | 'video' | 'audio' | 'document';
}

export function FlowPreviewModal({ nodes, edges, onClose }: FlowPreviewModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState('');
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const messageIdCounter = useRef(0); // Contador para IDs únicos
  const hasStarted = useRef(false); // Prevenir execução dupla

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = useCallback((type: 'bot' | 'user', content: string, mediaUrl?: string, mediaType?: 'image' | 'video' | 'audio' | 'document') => {
    console.log('💬 Adding message:', type, content);
    setMessages(prev => {
      messageIdCounter.current += 1; // Incrementar contador
      const newMessages = [
        ...prev,
        {
          id: `msg-${messageIdCounter.current}`, // ID único baseado em contador
          type,
          content,
          timestamp: new Date(),
          mediaUrl,
          mediaType
        },
      ];
      console.log('📝 Messages state updated. Total messages:', newMessages.length);
      return newMessages;
    });
  }, []);

  const processNode = useCallback((node: Node) => {
    console.log('🔍 Processing node:', node.id, 'Type:', node.data?.nodeType, 'Config:', node.data?.config);
    setCurrentNodeId(node.id);

    // Verificar se é nó de condição
    const isConditionNode = node.data?.nodeType === 'condition' ||
      node.data?.nodeType === 'switch' ||
      node.type === 'condition';

    if (isConditionNode) {
      // Aguardar resposta do usuário
      console.log('⏸️ Waiting for user input (condition node)');
      return;
    }

    // Se não é condição e não é trigger, é um nó de ação (text, image, video, etc)
    if (node.data?.nodeType !== 'trigger') {
      const config = node.data?.config || {};
      let content = '';

      // Extrair conteúdo baseado no tipo de ação
      // Prioridade: node.data.nodeType (definido no FlowBuilder) > config.actionType (legado)
      const actionType = node.data?.nodeType || config?.actionType || 'text';

      console.log('📝 Action Type:', actionType);
      console.log('⚙️ Config:', config);

      if (actionType === 'text') {
        // Tipo texto - verificar variações ou texto único
        if (config?.useTextVariations && config.textVariations && config.textVariations.length > 0) {
          // Escolher aleatoriamente uma variação
          const validVariations = config.textVariations.filter((v: string) => v && v.trim());
          if (validVariations.length > 0) {
            const randomIndex = Math.floor(Math.random() * validVariations.length);
            content = validVariations[randomIndex];
          } else {
            content = 'Mensagem de texto não configurada';
          }
        } else {
          // Verificar message ou content
          content = config?.message || config?.content || 'Mensagem de texto não configurada';
        }
        addMessage('bot', content);
      } else if (['image', 'video', 'audio', 'document'].includes(actionType)) {
        // Tipo mídia
        let mediaUrl = '';
        const mediaType = actionType as 'image' | 'video' | 'audio' | 'document';

        if (config.useMediaVariations && config.mediaVariations && config.mediaVariations.length > 0) {
          const validVariations = config.mediaVariations.filter((v: { url: string; caption?: string }) => v && v.url);
          if (validVariations.length > 0) {
            const randomIndex = Math.floor(Math.random() * validVariations.length);
            const variation = validVariations[randomIndex];
            content = variation.caption || '';
            mediaUrl = variation.url;
          } else {
            content = `⚠️ ${actionType.toUpperCase()} não configurado`;
          }
        } else {
          // Verificar se mediaUrl existe ou se estamos usando mediaUrl do preview
          mediaUrl = config.mediaUrl || config.url || config.preview || config.file;
          content = config.caption || '';
          
          if (!mediaUrl) {
             content = `⚠️ ${actionType.toUpperCase()} não configurado`;
          }
        }
        
        console.log('📤 Sending bot media:', mediaType, mediaUrl);
        addMessage('bot', content, mediaUrl, mediaType);

      } else if (actionType === 'openai' || actionType === 'groq' || actionType === 'ai') {
        const aiProvider = config.aiProvider || actionType;
        const prompt = config.prompt || 'Prompt não configurado';
        
        // Simulação de resposta da IA
        content = `🤖 IA (${aiProvider.toUpperCase()}): Processando...\n\nPrompt: "${prompt}"\n\n(No ambiente real, a IA geraria uma resposta baseada na mensagem do usuário e neste prompt)`;
        addMessage('bot', content);
      } else {
        content = `Ação: ${actionType}`;
        addMessage('bot', content);
      }
    }

    // Verificar próximo nó
    const nextEdge = edges.find(e => e.source === node.id);
    if (nextEdge) {
      const nextNode = nodes.find(n => n.id === nextEdge.target);
      
      // Se o próximo nó é uma condição, devemos avançar para ele
      // Mas o nó de condição em si também deve "pausar" para avaliar a resposta
      // Então aqui apenas avançamos para o nó de condição
      if (nextNode) {
        console.log('➡️ Advancing to next node (might be condition)');
        setTimeout(() => processNode(nextNode), 500);
      }
    } else {
      // Fim do fluxo
      console.log('✅ End of flow');
      setTimeout(() => {
        addMessage('bot', '✅ Fim do fluxo.');
      }, 500);
    }
  }, [nodes, edges, addMessage]);

  const startFlow = useCallback(() => {
    console.log('🚀 Starting flow with', nodes.length, 'nodes and', edges.length, 'edges');

    // Encontrar nó trigger
    const triggerNode = nodes.find(n => n.data?.nodeType === 'trigger');
    if (!triggerNode) {
      console.log('❌ No trigger node found');
      addMessage('bot', '⚠️ Nenhum nó Trigger encontrado no fluxo.');
      return;
    }

    console.log('✅ Found trigger node:', triggerNode.id);

    // Encontrar próximo nó após o trigger
    const nextEdge = edges.find(e => e.source === triggerNode.id);
    if (!nextEdge) {
      console.log('❌ Trigger not connected');
      addMessage('bot', '⚠️ Trigger não está conectado a nenhum nó.');
      return;
    }

    console.log('✅ Found next edge:', nextEdge.id, 'to', nextEdge.target);

    const nextNode = nodes.find(n => n.id === nextEdge.target);
    if (nextNode) {
      console.log('✅ Starting with node:', nextNode.id);
      // Pequeno delay para garantir que a UI está pronta
      setTimeout(() => processNode(nextNode), 100);
    }
  }, [nodes, edges, addMessage, processNode]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Iniciar o fluxo com o trigger - apenas uma vez
    if (!hasStarted.current) {
      hasStarted.current = true;
      startFlow();
    }
  }, [startFlow]);

  const handleSendMessage = () => {
    if (!userInput.trim() || isProcessing) return;

    setIsProcessing(true);
    const input = userInput.trim();
    addMessage('user', input);
    setUserInput('');

    // Processar resposta
    setTimeout(() => {
      processUserResponse(input);
      setIsProcessing(false);
    }, 300);
  };

  const processUserResponse = (input: string) => {
    if (!currentNodeId) {
      addMessage('bot', '⚠️ Nenhum nó ativo.');
      return;
    }

    // Encontrar próximas edges do nó atual
    const nextEdges = edges.filter(e => e.source === currentNodeId);

    if (nextEdges.length === 0) {
      addMessage('bot', '✅ Fim do fluxo.');
      return;
    }

    // Se tem condição
    const currentNode = nodes.find(n => n.id === currentNodeId);
    
    // Verificar se é nó de IA
    const isAINode = currentNode?.data?.nodeType === 'ai' || currentNode?.data?.nodeType === 'openai' || currentNode?.data?.nodeType === 'groq';

    if (isAINode) {
        // Simular comportamento da IA que analisa sentimento/resposta
        // No preview, vamos simular uma resposta simples baseada no input para permitir testar o fluxo
        console.log('🤖 Simulating AI response for input:', input);
        
        // Verificar se há uma próxima condição conectada
        if (nextEdges.length > 0) {
            const nextNode = nodes.find(n => n.id === nextEdges[0].target);
            if (nextNode) {
                // Se a IA deveria gerar uma resposta (ex: "sim" ou "não"), vamos usar o input do usuário 
                // como se fosse a saída da IA para continuar o fluxo
                setTimeout(() => processNode(nextNode), 500);
                return;
            }
        }
    }

    const isConditionNode = currentNode?.data?.nodeType === 'condition';

    if (isConditionNode) {
      const config = currentNode.data.config;
      const conditionType = config?.conditionType || config?.operator || 'contains';
      const value = config?.value?.toLowerCase() || '';
      const normalizedInput = input.toLowerCase();

      let conditionMet = false;

      // Usar a mesma lógica do backend
      switch (conditionType) {
        case 'equals':
        case '==':
          conditionMet = normalizedInput === value;
          break;
        case 'contains':
          conditionMet = normalizedInput.includes(value);
          break;
        case 'startsWith':
          conditionMet = normalizedInput.startsWith(value);
          break;
        case 'endsWith':
          conditionMet = normalizedInput.endsWith(value);
          break;
        case 'notEquals':
        case '!=':
          conditionMet = normalizedInput !== value;
          break;
        default:
          conditionMet = normalizedInput === value; // Padrão equals
      }

      console.log(`🔍 Condition check: "${normalizedInput}" ${conditionType} "${value}" = ${conditionMet}`);

      // Encontrar edge correspondente usando handleId
      const handleId = conditionMet ? 'true' : 'false';
      const edgeByHandle = nextEdges.find(e => e.sourceHandle === handleId);

      if (edgeByHandle) {
        console.log(`➡️ Following edge by handle: ${handleId}`);
        const nextNode = nodes.find(n => n.id === edgeByHandle.target);
        if (nextNode) {
          setTimeout(() => processNode(nextNode), 500);
          return;
        }
      }

      // Fallback: usar labels (legado)
      const targetEdge = nextEdges.find(e => {
        const label = e.label?.toString().toLowerCase();
        return conditionMet
          ? (label === 'true' || label === 'sim' || label === 'yes' || label === 'verdadeiro')
          : (label === 'false' || label === 'não' || label === 'no' || label === 'falso');
      });

      if (targetEdge) {
        const nextNode = nodes.find(n => n.id === targetEdge.target);
        if (nextNode) {
          setTimeout(() => processNode(nextNode), 500);
          return;
        }
      }

      // Se não encontrar caminho, parar (não usar fallback de primeira edge)
      console.log('⚠️ No matching path found for condition result');
      addMessage('bot', '⚠️ Fluxo interrompido: Nenhuma condição atendida.');
    } else {
      // Não é condição, seguir para próximo nó
      if (nextEdges.length > 0) {
        const nextNode = nodes.find(n => n.id === nextEdges[0].target);
        if (nextNode) {
          setTimeout(() => processNode(nextNode), 500);
          return;
        }
      }
    }

    addMessage('bot', '✅ Fim do fluxo.');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setUserInput(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const restartFlow = () => {
    setMessages([]);
    setCurrentNodeId(null);
    messageIdCounter.current = 0;
    
    // Pequeno delay para limpar o estado visual antes de reiniciar
    setTimeout(() => {
      startFlow();
    }, 100);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md h-[600px] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-brand-primary p-4 flex items-center justify-between text-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <span className="text-xl">🤖</span>
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">Preview do Bot</h3>
              <p className="text-xs text-white/80">Teste o fluxo antes de publicar</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={restartFlow}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/90 hover:text-white"
              title="Reiniciar conversa"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/90 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 mt-10">
            <p className="text-sm">Iniciando conversa...</p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[70%] rounded-2xl px-4 py-3 ${message.type === 'user'
                ? 'bg-purple-600 text-white rounded-br-none'
                : 'bg-white text-gray-800 shadow-sm rounded-bl-none border border-gray-200'
                }`}
            >
              {/* Media Rendering */}
              {message.mediaUrl && message.mediaType === 'image' && (
                <div className="mb-2 overflow-hidden rounded-lg">
                  <img 
                    src={message.mediaUrl} 
                    alt="Media" 
                    className="w-full h-auto object-cover max-h-48"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              {message.mediaUrl && message.mediaType === 'video' && (
                <div className="mb-2 overflow-hidden rounded-lg">
                  <video 
                    src={message.mediaUrl} 
                    controls 
                    className="w-full h-auto max-h-48"
                  />
                </div>
              )}

              {message.mediaUrl && message.mediaType === 'audio' && (
                <div className="mb-2">
                  <audio 
                    src={message.mediaUrl} 
                    controls 
                    className="w-full"
                  />
                </div>
              )}

              {message.mediaUrl && message.mediaType === 'document' && (
                <div className="mb-2 flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
                   <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm truncate">
                    Abrir Documento
                  </a>
                </div>
              )}

              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              <p className={`text-xs mt-1 ${message.type === 'user' ? 'text-purple-100' : 'text-gray-400'}`}>
                {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white relative">
        {/* Emoji Picker */}
        {showEmojiPicker && (
          <div ref={emojiPickerRef} className="absolute bottom-20 right-4 z-50">
            <EmojiPicker onEmojiClick={handleEmojiClick} />
          </div>
        )}

        <div className="flex space-x-2">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="px-3 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors"
            title="Adicionar emoji"
          >
            <span className="text-xl">😀</span>
          </button>
          <input
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem..."
            disabled={isProcessing}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 text-sm"
          />
          <button
            onClick={handleSendMessage}
            disabled={!userInput.trim() || isProcessing}
            className="px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Pressione Enter para enviar
        </p>
      </div>
    </div>
  </div>
);
}
